import React, { useEffect, useState } from "react";
import "../styles/consumer-onboarding.css";
import axios from "axios";

const formSchema = [
  {
    name: "businessName",
    label: "Business Name",
    type: "text",
    placeholder: "Enter business name",
    fullWidth: false,
  },
  {
    name: "contactName",
    label: "Contact Name",
    type: "text",
    placeholder: "Enter contact person name",
    fullWidth: false,
  },
  {
    name: "email",
    label: "Email Address",
    type: "email",
    placeholder: "Enter business email",
    fullWidth: false,
  },
  {
    name: "phone",
    label: "Phone Number",
    type: "text",
    placeholder: "Enter phone number",
    fullWidth: false,
  },
  {
    name: "businessLink",
    label: "Business Page / Website / Social Link",
    type: "text",
    placeholder: "Paste website or business page link",
    fullWidth: true,
  },
  {
    name: "category",
    label: "Business Category",
    type: "select",
    placeholder: "Select category",
    options: [
      "Retail",
      "Food",
      "Services",
      "Technology",
      "Education",
      "Health",
      "Manufacturing",
      "Other",
    ],
    fullWidth: false,
  },
  {
    name: "city",
    label: "City / Location",
    type: "text",
    placeholder: "Enter city or location",
    fullWidth: false,
  },
  {
    name: "supportNeeded",
    label: "Support Needed",
    type: "textarea",
    placeholder:
      "Tell us what kind of support you are looking for...",
    rows: 4,
    fullWidth: true,
  },
  {
    name: "description",
    label: "Short Business Description",
    type: "textarea",
    placeholder: "Briefly describe your business",
    rows: 5,
    fullWidth: true,
  },
];

const initialFormData = formSchema.reduce((acc, field) => {
  acc[field.name] = "";
  return acc;
}, {});

const ConsumerOnboarding = () => {
  const [formData, setFormData] = useState(initialFormData);

  // ✅ NEW STATES
  const [pageContent, setPageContent] = useState({});
  const [heroPoints, setHeroPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ✅ FETCH FROM BFF
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/2.0/enterprise-content`
        );

        if (res.data.success) {
          setPageContent(res.data.data.pageContent || {});
          setHeroPoints(res.data.data.heroPoints || []);
        }
      } catch (err) {
        console.error("Error loading content:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ SUBMIT TO BFF
  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/2.0/enterprise-register`,
        formData
      );

      if (res.data.success) {
        alert(
          "Your request has been submitted successfully. A confirmation email has been sent and our admin team will review it for approval."
        );

        setFormData(initialFormData);
      }
    } catch (error) {
      alert(error.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      name: field.name,
      value: formData[field.name],
      onChange: handleChange,
    };

    if (field.type === "select") {
      return (
        <select {...commonProps}>
          <option value="">{field.placeholder}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "textarea") {
      return (
        <textarea
          {...commonProps}
          rows={field.rows || 4}
          placeholder={field.placeholder}
        />
      );
    }

    return (
      <input
        {...commonProps}
        type={field.type}
        placeholder={field.placeholder}
      />
    );
  };

  // ✅ LOADING UI (no CSS change)
  if (loading) return <div className="consumer-onboarding-page">Loading...</div>;

  return (
    <div className="consumer-onboarding-page">
      <div className="consumer-glow consumer-glow-one" />
      <div className="consumer-glow consumer-glow-two" />

      <section className="consumer-hero">
        <div className="consumer-hero-content">
          <span className="consumer-pill">{pageContent.badge}</span>
          <h1>{pageContent.title}</h1>
          <p>{pageContent.description}</p>

          <div className="consumer-hero-points">
            {heroPoints.map((point) => (
              <div key={point.point_id} className="hero-point-card">
                <h3>{point.title}</h3>
                <p>{point.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="consumer-form-card">
          <div className="form-card-header">
            <span className="form-mini-label">{pageContent.formBadge}</span>
            <h2>{pageContent.formTitle}</h2>
            <p>{pageContent.formDescription}</p>
          </div>

          <form className="consumer-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              {formSchema.map((field) => (
                <div
                  key={field.name}
                  className={`form-field ${
                    field.fullWidth ? "form-field-full" : ""
                  }`}
                >
                  <label>{field.label}</label>
                  {renderField(field)}
                </div>
              ))}
            </div>

            <div className="form-submit-row">
              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Business Details"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ConsumerOnboarding;
